<script>
import { defineComponent } from 'vue'
import VTooltipWrapper from '../../common/v-tooltip-wrapper.vue'

export default defineComponent({
    name: 'disk-usage-meter',
    components: { VTooltipWrapper },
    props: {
        quota: {
            type: Number
            //default: -1
        },
        usage: {
            type: Number
            //default: 0
        },
        fileCount: Number,
        big: Boolean
    },
    data() {
        return {
            t_of: Lang.langGet('of'),
            t_in_use: Lang.langGet('in use'),
            t_unlimited: Lang.langGet('unlimited storage'),
            t_loading: Lang.langGet('Loading...')
        }
    },
    methods: {
        round(val) {
            return Math.round(val * 100) / 100
        }
    },
    computed: {
        loading() {
            return this.usage == undefined || this.quota == undefined
        },
        quotaInfinite() {
            return this.quota < 0
        },
        quotaZero() {
            return this.quota === 0
        },
        quotaInfiniteOrZero() {
            return this.quotaInfinite || this.quotaZero
        },
        usageRatio() {
            if (this.quotaInfiniteOrZero) return 0
            return this.usage / this.quota
        },
        usagePercent() {
            return Math.floor(this.usageRatio * 100)
        },
        usagePercentText() {
            return `${this.usagePercent}%`
        },
        usageFormatted() {
            let suffix = 'GiB'
            let usageInUnits = this.usage
            const usageKiB = this.usage * 0x10_0000
            if (usageKiB < 1024) {
                suffix = 'KiB'
                usageInUnits = usageKiB
            } else if (usageKiB < 0x10_0000) {
                suffix = 'MiB'
                usageInUnits *= 0x400
            }

            return `${suffix === 'GiB' ? this.round(usageInUnits) : Math.floor(usageInUnits)} ${suffix}`
        },
        barStyle() {
            return {
                '--disk-usage-ratio': this.usageRatio
            }
        },
        percentTextStyle() {
            return this.usageRatio < 0.5 ? {
                transform: 'translateX(calc(var(--disk-usage-ratio) * 100%))',
                'text-align': 'left'
            } : {
                transform: 'translateX(min(calc((1 - var(--disk-usage-ratio)) * -100%), 0%))',
                'text-align': 'right',
                color: 'white'
            }
        },
        t_filecount() {
            return `${this.fileCount} ${Lang.langChoice('File|Files', this.fileCount)}`
        }
    }
})
</script>

<template>
    <div :class="{'disk-usage-big': big}"
         class="disk-usage-container">
        <v-tooltip-wrapper>
            <template #activator="{ on, attrs }">
                <div :aria-valuenow="usagePercent"
                     :class="{
                        'shimmer-div': loading,
                        'progress-striped' : quotaInfiniteOrZero
                     }"
                     :style="barStyle"
                     aria-label="diskUsageLabel"
                     aria-valuemax="100"
                     aria-valuemin="0"
                     class="disk-usage-progressbar"
                     v-bind="attrs"
                     v-on="on">
                    <transition v-if="!loading"
                                appear
                                mode="out-in"
                                name="fade">
                        <div v-if="quotaInfinite"
                             key="unlimited"
                             class="disk-usage-percent-text"
                             style="text-align: center;">
                            {{ t_unlimited }}
                            <i class="far fa-circle-info" />
                        </div>
                        <div v-else-if="!quotaZero"
                             key="percent"
                             :style="percentTextStyle"
                             class="disk-usage-percent-text">
                            {{ usagePercentText }}
                            <i class="far fa-circle-info" />
                        </div>
                    </transition>
                </div>
            </template>
            <template v-if="!loading">
                <template v-if="fileCount != undefined">
                    {{ t_filecount }}
                </template>
                <div v-if="quotaInfinite"
                     id="diskUsageLabel"
                     key="unlimited">
                    {{ `${usageFormatted} ${t_in_use}` }}
                </div>
                <div v-else
                     id="diskUsageLabel"
                     key="percent">
                    {{ `${usageFormatted} ${t_of} ${quota} GiB ${t_in_use}` }}
                </div>
            </template>
            <template v-else>
                {{ t_loading }}
            </template>
        </v-tooltip-wrapper>
    </div>
</template>
